import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PagedDataGrid,
  FilterType,
  FilterOperator,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { formatDate, type IPagedCollection } from '@moko/utils';
import { FontIcon, Link } from '@fluentui/react';
import ImportMetadataPanel from './ImportMetadataPanel';
import ArchivedMetadatasPanel from './ArchivedMetadatasPanel';
import EntityCell from './EntityCell';
import { getMetadatasPaged } from '../../services/metadataService';
import AppContext from '../AppContext';
import { METADATA_STATUSES } from './metadataStatus';

const STATUS_FILTER_OPTIONS = METADATA_STATUSES.map((value) => ({
  value,
  label: value,
}));

const ENTITY_FILTER_OPTIONS = [
  'Asset',
  'Contractor',
  'Group',
  'Issue',
  'Order',
  'Rent',
  'Renter',
  'User',
  'Work',
  'Zone',
].map((value) => ({
  value,
  label: value,
}));

interface IMetadataRow {
  id: number;
  entity: string;
  name: string;
  status: string;
  statusDate: string;
  version: number;
  comment: string;
}

const PAGE_SIZE = 10;
const TITLE = 'Forms & Metadata';

const buildEditPath = (entity: string, name: string): string =>
  `/metadata/${encodeURIComponent(entity)}/${encodeURIComponent(name)}`;

const MetadataList = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);

  useEffect(() => {
    context.setSelectedTab(TITLE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshGrid = useCallback(() => gridRef.current?.refresh(), []);

  const columns: DataGridColumn<IMetadataRow>[] = useMemo(
    () => [
      {
        key: 'name',
        name: 'Name',
        width: 220,
        isSortable: true,
        filter: { enabled: true, type: FilterType.String },
        renderCell: (item) => (
          <Link onClick={() => navigate(buildEditPath(item.entity, item.name))}>
            {item.name}
          </Link>
        ),
      },
      {
        key: 'entity',
        name: 'Entity',
        width: 140,
        isSortable: true,
        filter: {
          enabled: true,
          type: FilterType.String,
          operators: [FilterOperator.Eq, FilterOperator.NEq, FilterOperator.In],
          options: ENTITY_FILTER_OPTIONS,
        },
        renderCell: (item) => <EntityCell entity={item.entity} />,
      },
      {
        key: 'status',
        name: 'Status',
        width: 110,
        isSortable: true,
        filter: {
          enabled: true,
          type: FilterType.String,
          operators: [FilterOperator.Eq, FilterOperator.NEq, FilterOperator.In],
          options: STATUS_FILTER_OPTIONS,
        },
      },
      {
        key: 'comment',
        name: 'Description',
        width: 260,
        ellipsis: true,
        renderCell: (item) => (
          <span
            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            title={item.comment || undefined}
          >
            {item.comment}
          </span>
        ),
      },
      {
        key: 'statusDate',
        name: 'Status date',
        width: 180,
      },
      {
        key: 'version',
        name: 'Version',
        width: 90,
      },
      {
        key: 'edit',
        name: '',
        width: 90,
        renderCell: (item) => (
          <Link
            onClick={() => navigate(buildEditPath(item.entity, item.name))}
            styles={{
              root: { display: 'inline-flex', alignItems: 'center', gap: 4 },
            }}
          >
            <FontIcon iconName="Edit" />
            <span>Edit</span>
          </Link>
        ),
      },
    ],
    [navigate]
  );

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: refreshGrid,
      },
      {
        key: 'create',
        text: 'Create',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/metadata/new'),
      },
      {
        key: 'import',
        text: 'Import',
        icon: <FontIcon iconName="CloudUpload" />,
        onClick: () => setImportOpen(true),
      },
      {
        key: 'archived',
        text: 'Archived',
        icon: <FontIcon iconName="Archive" />,
        onClick: () => setArchivedOpen(true),
      },
    ],
    [navigate, refreshGrid]
  );

  return (
    <div style={{ maxWidth: 1200 }}>
      <PagedDataGrid<IMetadataRow>
        ref={gridRef}
        columns={columns}
        filterEnabled
        commandBarItems={commandBarItems}
        pageSize={PAGE_SIZE}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          const res = await getMetadatasPaged(
            abortController,
            pageSize,
            pageNo,
            orderBy,
            search,
            filters
          );
          const items: IMetadataRow[] = (res?.items ?? []).map((item) => ({
            id: item.id,
            entity: item.entity,
            name: item.name,
            status: item.status,
            statusDate: item.statusDate
              ? formatDate(item.statusDate, true)
              : '',
            version: item.version,
            comment: item.comment ?? '',
          }));
          // BE may not return paging metadata yet; fall back to derived values.
          const isLastPage = res?.isLastPage ?? items.length < pageSize;
          const rowsCount =
            res?.rowsCount ?? (pageNo - 1) * pageSize + items.length;
          const pageCount =
            res?.pageCount ?? (isLastPage ? pageNo : pageNo + 1);
          return {
            items,
            isLastPage,
            pageCount,
            pageNo,
            pageSize,
            rowsCount,
          } satisfies IPagedCollection<IMetadataRow>;
        }}
        listHeigh={525}
      />
      <ImportMetadataPanel
        isOpen={importOpen}
        onDismiss={() => setImportOpen(false)}
        onImported={refreshGrid}
      />
      <ArchivedMetadatasPanel
        isOpen={archivedOpen}
        onDismiss={() => setArchivedOpen(false)}
        onChanged={refreshGrid}
      />
    </div>
  );
};

export default MetadataList;
